import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import { normListId } from "../../common/dualListBox";
import { staffToDropdownOptions } from "../../../services/lookupService";
import { mapStudyMetadataFromInfo } from "./studyFormMetadataUtils";
import { getStudyRowId } from "./studyMgmtReportUtils";

const pick = (obj, ...keys) => {
  if (obj == null) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

const normalizeToArray = (raw) => {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const vals = Object.values(raw);
    if (vals.length && typeof vals[0] === "object") return vals;
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === "object") return Object.values(parsed);
      return [];
    } catch {
      return [];
    }
  }
  return [];
};

const USER_ASSIGNMENT_KEYS = [
  "StudyUsers",
  "studyUsers",
  "UserList",
  "userList",
  "AssignedUsers",
  "assignedUsers",
];

const unwrapActivityPayload = (res) => {
  if (!res || typeof res !== "object") return {};
  let inner = res.data ?? res.Data;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    const nested = inner.data ?? inner.Data;
    if (
      nested &&
      typeof nested === "object" &&
      !Array.isArray(nested) &&
      (nested.StudyInfo != null ||
        nested.studyInfo != null ||
        nested.StudyUsers != null ||
        nested.studyUsers != null ||
        nested.UserList != null ||
        nested.userList != null)
    ) {
      const merged = { ...nested };
      for (const k of USER_ASSIGNMENT_KEYS) {
        const childArr = normalizeToArray(merged[k]);
        if (!childArr.length && inner[k] != null) {
          merged[k] = inner[k];
        }
      }
      inner = merged;
    }
    return inner;
  }
  if (
    res.StudyInfo != null ||
    res.studyInfo != null ||
    res.StudyUsers != null ||
    res.studyUsers != null
  ) {
    return res;
  }
  const table = res.Table ?? res.table;
  if (Array.isArray(table) && table[0] && typeof table[0] === "object") {
    return table[0];
  }
  return {};
};

const getFirstStudyInfo = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  const raw = pick(payload, "StudyInfo", "studyInfo", "Study", "study");
  if (Array.isArray(raw) && raw.length > 0) return raw[0];
  if (raw && typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (
    pick(payload, "StudyNumber", "studyNumber") != null ||
    pick(payload, "StudyId", "studyId") != null ||
    pick(payload, "StudyName", "studyName") != null
  ) {
    return payload;
  }
  return null;
};

const getStudyUsersList = (payload) => {
  if (!payload || typeof payload !== "object") return [];
  const raw = pick(
    payload,
    "StudyUsers",
    "studyUsers",
    "UserList",
    "userList",
    "AssignedUsers",
    "assignedUsers",
    "StudyStaffList",
    "studyStaffList",
  );
  return normalizeToArray(raw);
};

const getStudyUsersListFromStudyInfo = (info) => {
  if (!info || typeof info !== "object") return [];
  const raw = pick(
    info,
    "StudyUsers",
    "studyUsers",
    "UserList",
    "userList",
    "AssignedUsers",
    "assignedUsers",
  );
  return normalizeToArray(raw);
};

const mergeStudyUsersFromEnvelope = (data, res) => {
  if (!data || typeof data !== "object") return data;
  const hasUsers =
    getStudyUsersList(data).length > 0 ||
    getStudyUsersListFromStudyInfo(getFirstStudyInfo(data)).length > 0;
  if (hasUsers) return data;
  const fromRoot = [
    ...getStudyUsersList(res),
    ...(res?.data ? getStudyUsersList(res.data) : []),
    ...(res?.Data ? getStudyUsersList(res.Data) : []),
  ];
  if (!fromRoot.length) return data;
  return { ...data, StudyUsers: fromRoot };
};

const isAssignedUserRow = (u) => {
  if (u == null || typeof u !== "object") return false;
  const v = u.IsAssigned ?? u.isAssigned;
  if (v === undefined || v === null || v === "") return true;
  if (v === false || v === 0 || v === "0") return false;
  return (
    v === true ||
    v === 1 ||
    v === "1" ||
    String(v).toLowerCase() === "true" ||
    String(v).toLowerCase() === "yes"
  );
};

const pickUserRowStaffOrUserId = (u) =>
  pick(
    u,
    "UserId",
    "userId",
    "StaffId",
    "staffId",
    "StaffID",
    "StaffIDRef",
    "staffIDRef",
    "UserIdRef",
    "userIdRef",
  );

const parseMappedSiteIds = (raw) =>
  String(raw ?? "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

const idToUpperCase = (v) =>
  v === undefined || v === null || v === "" ? "" : String(v).toUpperCase();

function staffRawToDualList(staffList) {
  return staffToDropdownOptions(staffList).map((opt) => ({
    ...opt,
    userName:
      opt.staff?.StaffName ||
      `${opt.staff?.FirstName || ""} ${opt.staff?.LastName || ""}`.trim(),
    emailId: opt.staff?.EmailId || "",
  }));
}

function parseStudyNumberFields(rawValue, fallbackPrefix = "STD") {
  const raw = String(rawValue ?? "").trim();
  const digits = raw.replace(/\D/g, "");
  return {
    prefix: fallbackPrefix,
    studyNumber: digits ? digits.padStart(Math.max(5, digits.length), "0") : "",
  };
}

function findColorOption(studyColorOptions, colorValue) {
  const hex = String(colorValue || "").trim().toLowerCase();
  if (!hex) return null;
  return (
    (studyColorOptions || []).find(
      (opt) => String(opt.value).toLowerCase() === hex,
    ) || { value: colorValue, label: colorValue }
  );
}

function findStatusOption(studyStatusOptions, statusValue) {
  const status = String(statusValue || "").trim();
  if (!status) return null;
  if (/^archiv/i.test(status)) {
    return { value: "Archived", label: "Archived" };
  }
  return (
    (studyStatusOptions || []).find(
      (opt) => String(opt.value).toLowerCase() === status.toLowerCase(),
    ) || { value: status, label: status }
  );
}

function findDepartmentOption(departmentOptions, departmentValue) {
  const value = String(departmentValue ?? "").trim();
  if (!value) return "";
  const match = (departmentOptions || []).find(
    (opt) =>
      String(opt.value).toLowerCase() === value.toLowerCase() ||
      String(opt.label).toLowerCase() === value.toLowerCase(),
  );
  return match?.value ?? value;
}

function collectAssignedStaffIds(payload, staffListItems, rawEnvelope, info) {
  const ids = [];
  const seen = new Set();

  const pushRows = (rows, respectAssignFlag) => {
    if (!Array.isArray(rows)) return;
    for (const u of rows) {
      if (respectAssignFlag && !isAssignedUserRow(u)) continue;
      const id = normListId(pickUserRowStaffOrUserId(u));
      if (!id || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
  };

  const topUsers = [
    ...getStudyUsersList(payload),
    ...(rawEnvelope ? getStudyUsersList(rawEnvelope) : []),
    ...(rawEnvelope?.data ? getStudyUsersList(rawEnvelope.data) : []),
    ...(rawEnvelope?.Data ? getStudyUsersList(rawEnvelope.Data) : []),
  ];
  const nestedUsers = getStudyUsersListFromStudyInfo(info);
  const allUserRows = [...topUsers, ...nestedUsers];

  pushRows(allUserRows, true);
  if (!ids.length && allUserRows.length) {
    pushRows(allUserRows, false);
  }

  if (info) {
    const piId = normListId(
      pick(
        info,
        "PrincipalInvestigator",
        "principalInvestigator",
        "PI",
        "pi",
        "PIStaffId",
        "piStaffId",
      ),
    );
    if (piId && !seen.has(piId)) {
      seen.add(piId);
      ids.push(piId);
    }
  }

  return ids;
}

function mapRowFallbackForm(row, { prefix, studyColorOptions, studyStatusOptions }) {
  const numberFields = parseStudyNumberFields(
    pick(row, "StudyNumber", "studyNumber") || pick(row, "StudyNum", "studyNum"),
    prefix,
  );
  const piStaffId = String(
    pick(row, "PrincipalInvestigator", "principalInvestigator", "PIStaffId") || "",
  ).trim();

  return {
    studyId: getStudyRowId(row),
    studyTitle: pick(row, "StudyName", "studyName", "StudyTitle") || "",
    studyColor: findColorOption(
      studyColorOptions,
      pick(row, "StudySchColor", "studySchColor", "StudyColor", "studyColor"),
    ),
    prefix: numberFields.prefix,
    studyNumber: numberFields.studyNumber,
    principalInvestigator: null,
    eudractNumber: pick(row, "EudractNumber", "eudractNumber") || "",
    rdNumber: pick(row, "RDNumber", "rdNumber", "Randnumber", "randnumber") || "",
    department: pick(row, "Department", "department", "DepartmentName") || "",
    siteId: "",
    selectedSites: [],
    status: findStatusOption(studyStatusOptions, pick(row, "StudyStatus", "studyStatus")),
    sponsorName: pick(row, "SponsorName", "sponsorName") || "",
    irasNumber: pick(row, "IRASNumber", "irasNumber") || "",
    cordinatorName: pick(row, "CordinatorName", "cordinatorName") || "",
    cordinatorEmail: pick(row, "CordinatorEmail", "cordinatorEmail") || "",
    cordinatorContact:
      pick(row, "CordinatorContact", "cordinatorContact", "CordinatorPhone") || "",
    ...mapStudyMetadataFromInfo(row),
    _piStaffId: piStaffId,
  };
}

function mapInfoToModalForm(info, row, context) {
  const {
    prefix,
    studyColorOptions,
    studyStatusOptions,
    departmentOptions,
    siteOptions,
    staffRaw,
  } = context;

  const studyNumRaw =
    pick(info, "StudyNum", "studyNum", "StudyNumber", "studyNumber") ||
    pick(row, "StudyNumber", "studyNumber");
  const numberFields = parseStudyNumberFields(studyNumRaw, prefix);

  const piStaffId = String(
    pick(
      info,
      "PrincipalInvestigator",
      "principalInvestigator",
      "PI",
      "pi",
      "PIStaffId",
      "piStaffId",
    ) || "",
  ).trim();

  let mappedSiteIds = parseMappedSiteIds(
    pick(info, "MappedSiteIds", "mappedSiteIds"),
  );
  if (!mappedSiteIds.length) {
    const siteExtra = pick(
      info,
      "SiteID",
      "SiteId",
      "siteId",
      "siteID",
      "StudySiteID",
      "studySiteID",
    );
    if (siteExtra != null && siteExtra !== "") {
      mappedSiteIds = [String(siteExtra)];
    }
  }

  const selectedSites = (siteOptions || []).filter((opt) =>
    mappedSiteIds.some(
      (siteId) => String(siteId).toLowerCase() === String(opt.value).toLowerCase(),
    ),
  );

  const departmentValue = findDepartmentOption(
    departmentOptions,
    pick(info, "Department", "department"),
  );

  const piOption = piStaffId
    ? staffRawToDualList(
        (staffRaw || []).filter(
          (staff) => String(staff.StaffId).toLowerCase() === piStaffId.toLowerCase(),
        ),
      )[0] || null
    : null;

  return {
    studyId: getStudyRowId(row) || pick(info, "StudyId", "studyId") || "",
    studyTitle: pick(info, "StudyName", "studyName", "Study") || "",
    studyColor: findColorOption(
      studyColorOptions,
      pick(info, "StudyColor", "studyColor", "StudySchColor", "studySchColor"),
    ),
    prefix: numberFields.prefix,
    studyNumber: numberFields.studyNumber,
    principalInvestigator: piOption,
    eudractNumber: pick(info, "EudractNumber", "eudractNumber") || "",
    rdNumber: pick(info, "RandNumber", "randNumber", "RDNumber", "rdNumber") || "",
    department: departmentValue,
    siteId: selectedSites[0]?.value ? String(selectedSites[0].value) : "",
    selectedSites,
    status: findStatusOption(
      studyStatusOptions,
      pick(info, "StudyStatus", "studyStatus"),
    ),
    sponsorName: pick(info, "SponsorName", "sponsorName") || "",
    irasNumber: pick(info, "IRASNumber", "irasNumber") || "",
    cordinatorName:
      pick(info, "CordinatorName", "cordinatorName", "CoordinatorName") || "",
    cordinatorEmail:
      pick(info, "CordinatorEmail", "cordinatorEmail", "CoordinatorEmail") || "",
    cordinatorContact:
      pick(info, "CordinatorPhone", "cordinatorPhone", "CoordinatorPhone") || "",
    ...mapStudyMetadataFromInfo(info),
    _piStaffId: piStaffId,
  };
}

export async function loadStudyForModal({
  userData,
  row,
  departmentOptions = [],
  studyStatusOptions = [],
  studyColorOptions = [],
  siteOptions = [],
  staffRaw = [],
  prefix = "STD",
}) {
  const studyId = getStudyRowId(row);
  if (!studyId || !userData?.OrganizationId || !userData?.StaffId) {
    return {
      formData: mapRowFallbackForm(row, {
        prefix,
        studyColorOptions,
        studyStatusOptions,
      }),
      assignedStaff: [],
      initialAssignedStaffIds: [],
    };
  }

  const studyRes = await api.post(API_ENDPOINTS.STUDY.STUDY_ACTIVITY, {
    OrganizationId: idToUpperCase(userData.OrganizationId),
    CreatedBy: idToUpperCase(userData.StaffId),
    StaffIdRef: idToUpperCase(userData.StaffId),
    StudyId: idToUpperCase(studyId),
    Action: "GetStudyInfo",
  });

  const data = mergeStudyUsersFromEnvelope(
    unwrapActivityPayload(studyRes),
    studyRes,
  );
  const info = getFirstStudyInfo(data);
  const staffListItems = staffRawToDualList(staffRaw);

  const formData = info
    ? mapInfoToModalForm(info, row, {
        prefix,
        studyColorOptions,
        studyStatusOptions,
        departmentOptions,
        siteOptions,
        staffRaw,
      })
    : mapRowFallbackForm(row, { prefix, studyColorOptions, studyStatusOptions });

  const assignedIds = collectAssignedStaffIds(
    data,
    staffListItems,
    studyRes,
    info || row,
  );

  const assignedStaff = staffListItems.filter((item) =>
    assignedIds.some(
      (id) => String(id).toLowerCase() === String(item.value).toLowerCase(),
    ),
  );

  if (
    formData.principalInvestigator?.value &&
    !assignedStaff.some(
      (item) =>
        String(item.value).toLowerCase() ===
        String(formData.principalInvestigator.value).toLowerCase(),
    )
  ) {
    assignedStaff.unshift(formData.principalInvestigator);
  }

  const { _piStaffId, ...cleanFormData } = formData;

  return {
    formData: cleanFormData,
    assignedStaff,
    initialAssignedStaffIds: assignedIds.map((id) => normListId(id)).filter(Boolean),
  };
}
